// Website Inspector & Active Tool Detector.
// Scans any website URL (or the active product's domain) and auto-detects
// active analytics, tag managers, advertising pixels, session recording tools,
// payment gateways, CRMs, and CMS platforms.
import { useState, useEffect, type FormEvent } from "react";
import { api, ApiError, type DetectIntegrationsResponse, type DetectedIntegration } from "../../api";
import { IntegrationMark } from "../IntegrationMark";
import {
  GoogleConnectButton,
  GrantAccessInstead,
  ClarityConnectForm,
  PosthogConnectForm,
  StripeCredentialsForm,
} from "./forms";
import { deployGtmTag, GTM_DEPLOY_CONFIRM } from "../../lib/gtmDeploy";
import { adsOffered } from "../../lib/flags";

export function WebsiteDetector({
  slug,
  defaultUrl = "",
  products,
  onConnected,
  onDetectedTools,
  compact = false,
  autoScan = false,
  title = "Scan website for active tools & tracking",
  subtitle = "Auto-detect Google Analytics, Tag Manager, Clarity, PostHog, Meta Pixel, Stripe and other tools on your site.",
}: {
  slug?: string;
  defaultUrl?: string;
  products?: Array<{ slug: string; name: string; domain?: string | null }>;
  onConnected?: () => void;
  onDetectedTools?: (tools: DetectedIntegration[], response: DetectIntegrationsResponse) => void;
  compact?: boolean;
  autoScan?: boolean;
  title?: string;
  subtitle?: string;
}) {
  const [selectedSlug, setSelectedSlug] = useState<string>(slug ?? products?.[0]?.slug ?? "");
  const [url, setUrl] = useState<string>(() => {
    if (defaultUrl) return defaultUrl;
    if (slug && products) {
      const match = products.find((p) => p.slug === slug);
      if (match?.domain) return match.domain;
    }
    return "";
  });

  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DetectIntegrationsResponse | null>(null);

  // Active sub-forms for quick connection
  const [activeForm, setActiveForm] = useState<"clarity" | "posthog" | "grant_google" | "stripe" | null>(null);
  const [gtmDeploying, setGtmDeploying] = useState(false);
  const [gtmDeployNote, setGtmDeployNote] = useState<string | null>(null);

  // Auto-fill URL when selected slug changes
  const onSlugChange = (newSlug: string) => {
    setSelectedSlug(newSlug);
    if (products) {
      const match = products.find((p) => p.slug === newSlug);
      if (match?.domain && !url) {
        setUrl(match.domain);
      }
    }
  };

  const handleScan = async (targetUrl?: string) => {
    const raw = (targetUrl ?? url).trim();
    if (!raw && !selectedSlug) {
      setError("Please enter a website URL to scan.");
      return;
    }

    setScanning(true);
    setError(null);
    setActiveForm(null);
    setGtmDeployNote(null);

    try {
      const res = await api.detectIntegrations({
        slug: selectedSlug || undefined,
        url: raw || undefined,
      });
      setResult(res);
      if (!url && res.domain) {
        setUrl(res.domain);
      }
      onDetectedTools?.(res.integrations, res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setResult(null);
    } finally {
      setScanning(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void handleScan();
  };

  useEffect(() => {
    if (autoScan && (defaultUrl || (slug && products?.some((p) => p.slug === slug && p.domain)))) {
      void handleScan(defaultUrl || undefined);
    }
  }, []);

  const effectiveSlug = selectedSlug || slug || (products?.[0]?.slug ?? "");

  const deployGtm = async () => {
    if (!effectiveSlug) return;
    if (!confirm(GTM_DEPLOY_CONFIRM)) return;
    setGtmDeploying(true);
    setGtmDeployNote(null);
    try {
      const note = await deployGtmTag(effectiveSlug);
      setGtmDeployNote(note);
      onConnected?.();
    } catch (e) {
      setGtmDeployNote(e instanceof ApiError ? e.message : String(e));
    } finally {
      setGtmDeploying(false);
    }
  };

  return (
    <div
      className="panel"
      style={{
        padding: compact ? "16px 18px" : "20px 22px",
        borderColor: result ? "var(--keeper)" : undefined,
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: compact ? 14 : 15, display: "flex", alignItems: "center", gap: 8 }}>
            <span>{title}</span>
            {result && (
              <span className="eyebrow" style={{ color: "var(--keeper)", borderColor: "var(--keeper)" }}>
                {result.detected_count} detected
              </span>
            )}
          </div>
          <span className="sub" style={{ fontSize: 13, marginTop: 2, display: "block" }}>
            {subtitle}
          </span>
        </div>

        {result && (
          <button
            type="button"
            className="btn-ghost"
            style={{ fontSize: 12, padding: "4px 10px" }}
            onClick={() => void handleScan()}
            disabled={scanning}
          >
            {scanning ? "Rescanning…" : "Rescan"}
          </button>
        )}
      </div>

      <form onSubmit={submit} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        {products && products.length > 1 && (
          <select
            className="input"
            style={{ width: "auto", minWidth: 160 }}
            value={selectedSlug}
            onChange={(e) => onSlugChange(e.target.value)}
            disabled={scanning}
          >
            {products.map((p) => (
              <option key={p.slug} value={p.slug}>
                {p.name} ({p.slug})
              </option>
            ))}
          </select>
        )}

        <div style={{ flex: 1, minWidth: 220, position: "relative" }}>
          <input
            className="input"
            placeholder="e.g. https://motormerchants.com.au or example.com"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setError(null);
            }}
            disabled={scanning}
            spellCheck={false}
            autoComplete="off"
            aria-label="Website URL to scan"
          />
        </div>

        <button type="submit" className="btn-solid" disabled={scanning || (!url.trim() && !selectedSlug)}>
          {scanning ? (
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span className="pulse" />
              Scanning website…
            </span>
          ) : (
            "Scan site"
          )}
        </button>
      </form>

      {error && <div className="form-error">{error}</div>}

      {result && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 4 }}>
          <div
            style={{
              padding: "10px 14px",
              background: "var(--surface-inset)",
              borderRadius: "var(--radius-s)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 10,
              fontSize: 13,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 500 }}>{result.domain}</span>
              {result.platform && (
                <span
                  style={{
                    fontSize: 11,
                    padding: "2px 8px",
                    borderRadius: 10,
                    background: "var(--surface)",
                    border: "1px solid var(--border)",
                    color: "var(--text-2)",
                  }}
                >
                  Platform: {result.platform.name}
                </span>
              )}
              <span style={{ color: "var(--text-3)" }}>{result.summary}</span>
            </div>
            <span style={{ fontSize: 12, color: "var(--text-faint)" }} className="num">
              {result.final_url}
            </span>
          </div>

          {result.integrations.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--text-3)", padding: "10px 0" }}>
              No tracking scripts or tools were detected in the page source. If you use server-side tracking, database replication, or a tag manager loaded asynchronously, you can still connect below.
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: compact ? "1fr" : "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 10,
              }}
            >
              {result.integrations.map((tool) => (
                <DetectedToolCard
                  key={tool.key}
                  tool={tool}
                  slug={effectiveSlug}
                  onDeployGtm={() => void deployGtm()}
                  gtmDeploying={gtmDeploying}
                  onOpenClarity={() => setActiveForm((f) => (f === "clarity" ? null : "clarity"))}
                  onOpenPosthog={() => setActiveForm((f) => (f === "posthog" ? null : "posthog"))}
                  onOpenGrantGoogle={() => setActiveForm((f) => (f === "grant_google" ? null : "grant_google"))}
                  onOpenStripe={() => setActiveForm((f) => (f === "stripe" ? null : "stripe"))}
                />
              ))}
            </div>
          )}

          {gtmDeployNote && (
            <div style={{ fontSize: 12.5, color: "var(--text-2)", marginTop: 4 }}>
              {gtmDeployNote}
            </div>
          )}

          {/* Quick-connect popouts for detected tools */}
          {activeForm === "clarity" && effectiveSlug && (
            <div className="panel" style={{ background: "var(--surface-inset)", marginTop: 6 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontWeight: 500, fontSize: 13 }}>Connect Microsoft Clarity</span>
                <button type="button" className="btn-text" onClick={() => setActiveForm(null)}>✕ Close</button>
              </div>
              <ClarityConnectForm
                slug={effectiveSlug}
                onDone={() => {
                  setActiveForm(null);
                  onConnected?.();
                  void handleScan();
                }}
              />
            </div>
          )}

          {activeForm === "posthog" && effectiveSlug && (
            <div className="panel" style={{ background: "var(--surface-inset)", marginTop: 6 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontWeight: 500, fontSize: 13 }}>Connect PostHog</span>
                <button type="button" className="btn-text" onClick={() => setActiveForm(null)}>✕ Close</button>
              </div>
              <PosthogConnectForm
                slug={effectiveSlug}
                onDone={() => {
                  setActiveForm(null);
                  onConnected?.();
                  void handleScan();
                }}
              />
            </div>
          )}

          {activeForm === "stripe" && effectiveSlug && (
            <div className="panel" style={{ background: "var(--surface-inset)", marginTop: 6 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontWeight: 500, fontSize: 13 }}>Connect Stripe</span>
                <button type="button" className="btn-text" onClick={() => setActiveForm(null)}>✕ Close</button>
              </div>
              <StripeCredentialsForm
                slug={effectiveSlug}
                onDone={() => {
                  setActiveForm(null);
                  onConnected?.();
                  void handleScan();
                }}
              />
            </div>
          )}

          {activeForm === "grant_google" && effectiveSlug && (
            <div className="panel" style={{ background: "var(--surface-inset)", marginTop: 6 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontWeight: 500, fontSize: 13 }}>Grant Google Service Account</span>
                <button type="button" className="btn-text" onClick={() => setActiveForm(null)}>✕ Close</button>
              </div>
              <GrantAccessInstead
                slug={effectiveSlug}
                kinds={["ga4", "gtm"]}
                onDone={(note) => {
                  setActiveForm(null);
                  setGtmDeployNote(note);
                  onConnected?.();
                  void handleScan();
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DetectedToolCard({
  tool,
  slug,
  onDeployGtm,
  gtmDeploying,
  onOpenClarity,
  onOpenPosthog,
  onOpenGrantGoogle,
  onOpenStripe,
}: {
  tool: DetectedIntegration;
  slug: string;
  onDeployGtm: () => void;
  gtmDeploying: boolean;
  onOpenClarity: () => void;
  onOpenPosthog: () => void;
  onOpenGrantGoogle: () => void;
  onOpenStripe: () => void;
}) {
  const isConnected = tool.is_connected;
  const isPending = tool.connection_status === "pending";
  const isFkSupported = (tool.key === "google_ads" || tool.fk_kind === "google_ads")
    ? (tool.fk_supported && adsOffered)
    : tool.fk_supported;

  return (
    <div
      style={{
        padding: "12px 14px",
        background: "var(--surface)",
        border: isConnected
          ? "1px solid var(--pos)"
          : isFkSupported
          ? "1px solid var(--border-hover)"
          : "1px solid var(--border)",
        borderRadius: "var(--radius-s)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <IntegrationMark kind={tool.fk_kind ?? tool.key} size={20} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 500, fontSize: 13, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span>{tool.name}</span>
            <span
              style={{
                fontSize: 10,
                textTransform: "uppercase",
                padding: "1px 5px",
                borderRadius: 4,
                background: "var(--surface-inset)",
                color: "var(--text-3)",
              }}
            >
              {tool.category.replace("_", " ")}
            </span>
          </div>
          {tool.details && (
            <div className="num" style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 1 }}>
              {tool.details}
            </div>
          )}
        </div>

        {isConnected ? (
          <span className="chip" style={{ background: "var(--pos-bg)" }}>
            <span className="chip-dot" style={{ background: "var(--pos)" }} />
            <span className="chip-label" style={{ color: "var(--pos)", fontSize: 11 }}>
              Connected
            </span>
          </span>
        ) : isPending ? (
          <span className="chip" style={{ background: "var(--warn-bg)" }}>
            <span className="chip-dot" style={{ background: "var(--warn)" }} />
            <span className="chip-label" style={{ color: "var(--warn)", fontSize: 11 }}>
              Pending
            </span>
          </span>
        ) : isFkSupported ? (
          <span className="chip" style={{ background: "var(--keeper-bg)" }}>
            <span className="chip-dot" style={{ background: "var(--keeper)" }} />
            <span className="chip-label" style={{ color: "var(--keeper)", fontSize: 11 }}>
              Ready to connect
            </span>
          </span>
        ) : (
          <span
            style={{
              fontSize: 11,
              color: "var(--text-faint)",
              padding: "2px 6px",
              background: "var(--surface-inset)",
              borderRadius: 4,
            }}
          >
            Detected
          </span>
        )}
      </div>

      {tool.recommendation && !isConnected && (
        <div style={{ fontSize: 12, color: "var(--text-3)" }}>
          {tool.recommendation}
        </div>
      )}

      {/* Quick Action buttons */}
      {!isConnected && isFkSupported && slug && (
        <div style={{ display: "flex", gap: 8, marginTop: 2, flexWrap: "wrap", alignItems: "center" }}>
          {(tool.key === "ga4" || tool.key === "gtm" || (tool.key === "google_ads" && adsOffered)) && (
            <>
              <GoogleConnectButton slug={slug} kinds={[tool.key]} />
              <button
                type="button"
                className="btn-text"
                style={{ fontSize: 12, padding: 0 }}
                onClick={onOpenGrantGoogle}
              >
                Service account
              </button>
            </>
          )}

          {tool.key === "gtm" && (
            <button
              type="button"
              className="btn-ghost"
              style={{ fontSize: 12, padding: "3px 8px" }}
              disabled={gtmDeploying}
              onClick={onDeployGtm}
            >
              {gtmDeploying ? "Deploying…" : "Deploy FunnelKeeper to GTM"}
            </button>
          )}

          {tool.key === "clarity" && (
            <button
              type="button"
              className="btn-solid"
              style={{ fontSize: 12, padding: "4px 10px" }}
              onClick={onOpenClarity}
            >
              Connect Clarity
            </button>
          )}

          {tool.key === "posthog" && (
            <button
              type="button"
              className="btn-solid"
              style={{ fontSize: 12, padding: "4px 10px" }}
              onClick={onOpenPosthog}
            >
              Connect PostHog
            </button>
          )}

          {tool.key === "stripe" && (
            <button
              type="button"
              className="btn-solid"
              style={{ fontSize: 12, padding: "4px 10px" }}
              onClick={onOpenStripe}
            >
              Connect Stripe
            </button>
          )}
        </div>
      )}
    </div>
  );
}
